import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Chip,
  Avatar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GavelIcon from '@mui/icons-material/Gavel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { supabase } from '../../services/supabase';
import type { Player } from '../../types/models';
import { applyAdminFilter, fixTypes } from '../../services/adminPermissions';

const PlayersAdmin: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [nickname, setNickname] = useState('');
  const [fullName, setFullName] = useState('');
  const [isJudge, setIsJudge] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);

  // Fetch players on component mount
  useEffect(() => {
    fetchPlayers();
  }, []);

  // Filter players when search query changes
  useEffect(() => {
    if (searchQuery) {
      const lowercaseQuery = searchQuery.toLowerCase();
      const filtered = players.filter(
        player => 
          player.nickname.toLowerCase().includes(lowercaseQuery) ||
          player.full_name.toLowerCase().includes(lowercaseQuery)
      );
      setFilteredPlayers(filtered);
    } else {
      setFilteredPlayers(players);
    }
  }, [searchQuery, players]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      // Players table doesn't have club_id column,
      // so we don't need to filter by club (all users can see all players)
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .order('nickname', { ascending: true });

      if (error) {
        throw error;
      }

      // Fix types to ensure proper typing
      const typedData = fixTypes<Player>(data || []);
      setPlayers(typedData);
      setFilteredPlayers(typedData);
    } catch (error: any) {
      console.error('Error fetching players:', error.message);
      showSnackbar('Failed to load players', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (player?: Player) => {
    if (player) {
      setEditingPlayerId(player.id);
      setNickname(player.nickname);
      setFullName(player.full_name);
      setIsJudge(player.is_judge);
      setProfilePictureUrl(player.profile_picture_url || null);
      setProfilePicturePreview(player.profile_picture_url || null);
    } else {
      setEditingPlayerId(null);
      setNickname('');
      setFullName('');
      setIsJudge(false);
      setProfilePictureUrl(null);
      setProfilePicturePreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const resetForm = () => {
    setNickname('');
    setFullName('');
    setIsJudge(false);
    setEditingPlayerId(null);
    setProfilePicture(null);
    setProfilePictureUrl(null);
    setProfilePicturePreview(null);
  };
  
  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setProfilePicture(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadProfilePicture = async (): Promise<string | null> => {
    if (!profilePicture) {
      return profilePictureUrl; // Return existing URL if no new file
    }

    setUploadLoading(true);
    try {
      const fileExt = profilePicture.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `player-profiles/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('players')
        .upload(filePath, profilePicture);
        
      if (uploadError) {
        throw uploadError;
      }
      
      // Get the public URL
      const { data } = supabase.storage
        .from('players')
        .getPublicUrl(filePath);
        
      return data.publicUrl;
    } catch (error: any) {
      console.error('Error uploading profile picture:', error.message);
      showSnackbar('Failed to upload profile picture.', 'error');
      return null;
    } finally {
      setUploadLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async () => {
    if (!nickname.trim()) {
      showSnackbar('Nickname is required', 'error');
      return;
    }

    if (!fullName.trim()) {
      showSnackbar('Full name is required', 'error');
      return;
    }

    try {
      // Upload profile picture if selected
      const profilePictureUrl = await uploadProfilePicture();
      
      // Get current user role to check for club_id
      const { getCurrentUserRole } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      
      // Prepare player data with club_id if applicable
      const playerData: any = {
        nickname,
        full_name: fullName,
        is_judge: isJudge
      };
      
      // Add profile picture URL if available
      if (profilePictureUrl) {
        playerData.profile_picture_url = profilePictureUrl;
      }
      
      // We don't need to add club_id as the column doesn't exist in the players table
      // This was causing an error in the previous implementation
      
      if (editingPlayerId) {
        // Update existing player
        // First check if the admin has permission to update this player
        let updateQuery = supabase
          .from('players')
          .update(playerData);
          
        // No need to filter by club_id as it doesn't exist in the players table
        
        const { error } = await updateQuery.eq('id', editingPlayerId);

        if (error) throw error;
        showSnackbar('Player updated successfully', 'success');
      } else {
        // Create new player
        const { error } = await supabase
          .from('players')
          .insert([playerData]);

        if (error) throw error;
        showSnackbar('Player created successfully', 'success');
      }

      handleCloseDialog();
      fetchPlayers();
    } catch (error: any) {
      console.error('Error saving player:', error.message);
      showSnackbar(`Failed to ${editingPlayerId ? 'update' : 'create'} player`, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this player? This will also delete all associated game results.')) {
      return;
    }

    try {
      // Get current user role to check permissions
      const { getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      const superAdmin = await isSuperAdmin();
      
      // Build the delete query
      let query = supabase
        .from('players')
        .delete();
      
      // No need to filter by club_id as it doesn't exist in the players table
      
      // Add the final ID filter
      const { error } = await query.eq('id', id);

      if (error) throw error;

      showSnackbar('Player deleted successfully', 'success');
      fetchPlayers();
    } catch (error: any) {
      console.error('Error deleting player:', error.message);
      showSnackbar('Failed to delete player. They may have associated game records.', 'error');
    }
  };

  const toggleJudgeStatus = async (id: string, currentStatus: boolean) => {
    try {
      // Get current user role to check permissions
      const { getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      const userRole = await getCurrentUserRole();
      const superAdmin = await isSuperAdmin();
      
      // Build the update query
      let query = supabase
        .from('players')
        .update({ is_judge: !currentStatus });
      
      // No need to filter by club_id as it doesn't exist in the players table
      
      // Add the final ID filter
      const { error } = await query.eq('id', id);

      if (error) throw error;

      showSnackbar(`Player ${currentStatus ? 'removed from' : 'promoted to'} judge`, 'success');
      fetchPlayers();
    } catch (error: any) {
      console.error('Error updating judge status:', error.message);
      showSnackbar('Failed to update judge status', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Manage Players
        </Typography>
        <Button 
          variant="contained" 
          color="secondary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add Player
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search players by name or nickname..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredPlayers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>
            {searchQuery ? 'No players match your search.' : 'No players found. Create your first player!'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Nickname</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlayers.map((player) => (
                <TableRow key={player.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={player.profile_picture_url || undefined} 
                        alt={player.nickname}
                        sx={{ mr: 2, width: 40, height: 40 }}
                      >
                        {player.nickname.charAt(0).toUpperCase()}
                      </Avatar>
                      {player.nickname}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {player.full_name}
                  </TableCell>
                  <TableCell>
                    {player.is_judge ? (
                      <Chip 
                        icon={<GavelIcon />} 
                        label="Judge" 
                        color="primary" 
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => toggleJudgeStatus(player.id, player.is_judge)} 
                      color={player.is_judge ? 'default' : 'primary'} 
                      size="small"
                      title={player.is_judge ? "Remove judge status" : "Make judge"}
                    >
                      <GavelIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleOpenDialog(player)} 
                      color="primary" 
                      size="small"
                      title="Edit player"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(player.id)} 
                      color="error" 
                      size="small"
                      title="Delete player"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog for adding/editing player */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlayerId ? 'Edit Player' : 'Add New Player'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3, mt: 2 }}>
            <Box sx={{ position: 'relative', width: 120, height: 120 }}>
              <Avatar
                src={profilePicturePreview || undefined}
                alt={nickname || 'Profile Picture'}
                sx={{ width: '100%', height: '100%' }}
              >
                {nickname ? nickname.charAt(0).toUpperCase() : 'P'}
              </Avatar>
              <input
                type="file"
                id="profile-picture-input"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfilePictureChange}
              />
              <label htmlFor="profile-picture-input">
                <IconButton
                  component="span"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <PhotoCameraIcon />
                </IconButton>
              </label>
            </Box>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            id="nickname"
            label="Nickname"
            type="text"
            fullWidth
            variant="outlined"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            margin="dense"
            id="fullName"
            label="Full Name"
            type="text"
            fullWidth
            variant="outlined"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isJudge}
                onChange={(e) => setIsJudge(e.target.checked)}
                color="primary"
              />
            }
            label="Is Judge"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="secondary"
            sx={{ borderRadius: 2 }}
            disabled={uploadLoading}
          >
            {uploadLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              editingPlayerId ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlayersAdmin;